/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Collapse,
} from '@mui/material';
import { Icon } from '@iconify/react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { getActivityMeta } from './timelineConfig';
import { getStageForStatus } from '../pipeline/pipelineConfig';

dayjs.extend(relativeTime);

const ActivityTimelineItem = ({ item, isLast }) => {
  const [expanded, setExpanded] = useState(false);
  const meta = getActivityMeta(item.activityType);

  const authorName = item.createdBy
    ? `${item.createdBy.firstName || ''} ${item.createdBy.lastName || ''}`.trim()
    : 'System';

  const displayDate = item.activityType === 'document_request' ? (item.modifiedOn || item.createdOn) : item.createdOn;
  const timeAgo = dayjs(displayDate).fromNow();
  const fullDate = dayjs(displayDate).format('MMM D, YYYY h:mm A');

  // Render the content based on activity type
  const renderContent = () => {
    switch (item.activityType) {
      case 'note':
        return (
          <>
            <Typography variant="subtitle2" fontWeight={600}>
              {item.subject}
            </Typography>
            {item.note && (
              <>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: expanded ? 'unset' : 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {item.note}
                </Typography>
                {item.note.length > 100 && (
                  <Typography
                    variant="caption"
                    color="primary"
                    sx={{ cursor: 'pointer', mt: 0.5, display: 'inline-block' }}
                    onClick={() => setExpanded(!expanded)}
                  >
                    {expanded ? 'Show less' : 'Show more'}
                  </Typography>
                )}
              </>
            )}
          </>
        );

      case 'call':
        return (
          <>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="subtitle2" fontWeight={600}>
                {item.subject}
              </Typography>
              <Chip
                label={item.status}
                size="small"
                sx={{
                  fontSize: '0.7rem',
                  height: 22,
                  bgcolor: item.status === 'Held' ? '#54D62C18' : '#FFC10718',
                  color: item.status === 'Held' ? '#229A16' : '#B78103',
                }}
              />
              {item.duration && (
                <Typography variant="caption" color="text.disabled">
                  {item.duration}
                </Typography>
              )}
            </Box>
            {item.note && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {item.note.length > 100 && !expanded
                  ? `${item.note.substring(0, 100)}...`
                  : item.note}
              </Typography>
            )}
          </>
        );

      case 'meeting':
        return (
          <>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="subtitle2" fontWeight={600}>
                {item.subject}
              </Typography>
              <Chip
                label={item.status}
                size="small"
                sx={{ fontSize: '0.7rem', height: 22 }}
              />
            </Box>
            <Box display="flex" gap={2} mt={0.5} flexWrap="wrap">
              {item.location && (
                <Typography variant="caption" color="text.secondary">
                  <Icon icon="mdi:map-marker" style={{ fontSize: 12, verticalAlign: 'middle' }} />{' '}
                  {item.location}
                </Typography>
              )}
              {item.startDate && (
                <Typography variant="caption" color="text.secondary">
                  {dayjs(item.startDate).format('MMM D, h:mm A')}
                  {item.endDate && ` - ${dayjs(item.endDate).format('h:mm A')}`}
                </Typography>
              )}
            </Box>
            {item.note && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {item.note.length > 100 && !expanded
                  ? `${item.note.substring(0, 100)}...`
                  : item.note}
              </Typography>
            )}
          </>
        );

      case 'email':
        return (
          <>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="caption" color="text.disabled">
                To: {item.receiver}
              </Typography>
            </Box>
            <Typography variant="subtitle2" fontWeight={600}>
              {item.subject}
            </Typography>
            {item.message && (
              <>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: expanded ? 'unset' : 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {item.message}
                </Typography>
                {item.message.length > 100 && (
                  <Typography
                    variant="caption"
                    color="primary"
                    sx={{ cursor: 'pointer', mt: 0.5, display: 'inline-block' }}
                    onClick={() => setExpanded(!expanded)}
                  >
                    {expanded ? 'Show less' : 'Show more'}
                  </Typography>
                )}
              </>
            )}
          </>
        );

      case 'task':
        return (
          <>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="subtitle2" fontWeight={600}>
                {item.subject}
              </Typography>
              {item.status && (
                <Chip
                  label={item.status}
                  size="small"
                  sx={{ fontSize: '0.7rem', height: 22 }}
                />
              )}
              {item.priority && (
                <Chip
                  label={item.priority}
                  size="small"
                  sx={{
                    fontSize: '0.7rem',
                    height: 22,
                    bgcolor: item.priority === 'High' ? '#FF484218' : '#919EAB18',
                    color: item.priority === 'High' ? '#FF4842' : '#637381',
                  }}
                />
              )}
            </Box>
            <Box display="flex" gap={2} mt={0.5} flexWrap="wrap">
              {item.assignTo && (
                <Typography variant="caption" color="text.secondary">
                  Assigned to: {item.assignTo.firstName} {item.assignTo.lastName}
                </Typography>
              )}
              {item.startDate && (
                <Typography variant="caption" color="text.secondary">
                  Due: {dayjs(item.startDate).format('MMM D, YYYY')}
                </Typography>
              )}
            </Box>
            {item.note && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {item.note.length > 100 && !expanded
                  ? `${item.note.substring(0, 100)}...`
                  : item.note}
              </Typography>
            )}
          </>
        );

      case 'status_change': {
        const prevStage = getStageForStatus(item.previousStatus);
        const newStage = getStageForStatus(item.newStatus);
        return (
          <>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Chip
                label={item.previousStatus}
                size="small"
                sx={{
                  bgcolor: `${prevStage.color}18`,
                  color: prevStage.color,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  borderColor: prevStage.color,
                }}
                variant="outlined"
              />
              <Icon icon="mdi:arrow-right" style={{ fontSize: 14, color: '#919EAB' }} />
              <Chip
                label={item.newStatus}
                size="small"
                sx={{
                  bgcolor: `${newStage.color}18`,
                  color: newStage.color,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  borderColor: newStage.color,
                }}
                variant="outlined"
              />
            </Box>
            {item.reasonLost && (
              <Box mt={1} p={1} bgcolor="#FF484212" borderRadius={1}>
                <Typography variant="caption" color="error.dark">
                  <strong>Reason Lost:</strong> {item.reasonLost}
                </Typography>
              </Box>
            )}
            {item.transitionNote && (
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5} fontStyle="italic">
                {item.transitionNote}
              </Typography>
            )}
          </>
        );
      }

      case 'sms':
        return (
          <>
            <Box display="flex" alignItems="center" gap={1}>
              <Icon
                icon={item.direction === 'outbound' ? 'mdi:arrow-top-right' : 'mdi:arrow-bottom-left'}
                style={{
                  fontSize: 14,
                  color: item.direction === 'outbound' ? '#1890FF' : '#54D62C',
                }}
              />
              <Typography variant="caption" color="text.disabled">
                {item.direction === 'outbound' ? `To: ${item.to}` : `From: ${item.from}`}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {item.body}
            </Typography>
          </>
        );

      case 'document_request': {
        const statusColorMap = {
          'Added': { bg: '#919EAB18', color: '#637381' },
          'Requested': { bg: '#1890FF18', color: '#1890FF' },
          'Received': { bg: '#FFC10718', color: '#B78103' },
          'Uploaded to Encompass': { bg: '#54D62C18', color: '#229A16' },
          'Rejected': { bg: '#FF484218', color: '#FF4842' },
        };
        const sc = statusColorMap[item.status] || statusColorMap.Added;
        return (
          <>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="subtitle2" fontWeight={600}>
                {item.documentName}
              </Typography>
              <Chip
                label={item.status}
                size="small"
                sx={{ fontSize: '0.7rem', height: 22, bgcolor: sc.bg, color: sc.color, fontWeight: 600 }}
              />
              <Chip
                label={item.category}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.65rem', height: 20 }}
              />
            </Box>
            {item.attachments && item.attachments.length > 0 && (
              <Box display="flex" gap={0.5} mt={0.5} flexWrap="wrap">
                {item.attachments.map((att, i) => (
                  <Chip
                    key={att.attachmentId || i}
                    icon={<Icon icon="mdi:paperclip" style={{ fontSize: 12 }} />}
                    label={att.fileName || 'Attachment'}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.65rem', height: 20 }}
                  />
                ))}
              </Box>
            )}
            {item.comment && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {item.comment}
              </Typography>
            )}
            {item.rejectionReason && (
              <Box mt={0.5} p={0.75} bgcolor="#FF484212" borderRadius={1}>
                <Typography variant="caption" color="error.dark">
                  <strong>Rejected:</strong> {item.rejectionReason}
                </Typography>
              </Box>
            )}
          </>
        );
      }

      default:
        return (
          <Typography variant="body2" color="text.secondary">
            Activity recorded
          </Typography>
        );
    }
  };

  return (
    <Box sx={{ display: 'flex', mb: 2, position: 'relative' }}>
      {/* Timeline connector line */}
      {!isLast && (
        <Box
          sx={{
            width: 2,
            bgcolor: 'grey.200',
            position: 'absolute',
            left: 17,
            top: 36,
            bottom: -16,
          }}
        />
      )}

      {/* Activity icon */}
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          bgcolor: `${meta.color}14`,
          border: `2px solid ${meta.color}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          mr: 2,
          zIndex: 1,
        }}
      >
        <Icon icon={meta.icon} style={{ fontSize: 18, color: meta.color }} />
      </Box>

      {/* Content card */}
      <Box
        sx={{
          flex: 1,
          bgcolor: 'background.paper',
          borderRadius: 1.5,
          p: 1.5,
          border: '1px solid',
          borderColor: 'grey.200',
          borderLeft: `3px solid ${meta.color}`,
          '&:hover': {
            bgcolor: 'grey.50',
          },
        }}
      >
        {renderContent()}

        {/* Metadata row */}
        <Box display="flex" alignItems="center" gap={1} mt={1}>
          <Chip
            label={meta.label}
            size="small"
            sx={{
              fontSize: '0.65rem',
              height: 20,
              bgcolor: `${meta.color}14`,
              color: meta.color,
              fontWeight: 600,
            }}
          />
          <Typography variant="caption" color="text.disabled">
            {authorName}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            &middot;
          </Typography>
          <Typography variant="caption" color="text.disabled" title={fullDate}>
            {timeAgo}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ActivityTimelineItem;
